// App page — every deployable service (job) under one app, with the current
// state per environment at a glance. Replaces level 1 of the legacy JobIdModal.

import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Construction as JobsIcon,
  GridView as ServicesIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { useV2Filters } from '../hooks/useV2Filters';
import { useTableSort } from '../hooks/useTableSort';
import {
  groupServices,
  isActiveDeploy,
  ServiceSortKey,
  sortEnvNames,
  sortServiceGroups,
  ts,
  VALID_SERVICE_SORT_KEYS,
} from '../lib/grouping';
import { getColors, rounded } from '../theme/tokens';
import PageHeader from '../components/PageHeader';
import StatusDot from '../components/StatusDot';
import StatusPill from '../components/StatusPill';
import TypeBadge from '../components/TypeBadge';
import ServiceCard from '../components/ServiceCard';
import DeploymentRow from '../components/DeploymentRow';
import EmptyState from '../components/EmptyState';
import { getStatusHue } from '../theme/statusTokens';
import { timeAgo } from '../lib/format';
import { deploymentKey, serviceCompareLabel, serviceFilterKey, serviceHref } from '../lib/records';

const AppPage: React.FC = () => {
  const { app = '' } = useParams<{ app: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { records, staleCurrentIds } = useDeploymentData();

  // Include dynamic-env records so ephemeral deploys of this app are visible,
  // but drop stale/superseded failed & stuck-deploying records.
  const appRecords = useMemo(
    () => records.filter(r => r.app === app && (!r.id || !staleCurrentIds.has(r.id))),
    [records, app, staleCurrentIds]
  );
  const services = useMemo(() => groupServices(appRecords), [appRecords]);

  // In-progress deploys for this app — surfaced prominently up top, same as
  // OverviewPage (dashboard-wide) and ServicePage (single service).
  const deployingRecords = useMemo(
    () =>
      appRecords
        .filter(r => isActiveDeploy(r))
        .sort((a, b) => ts(b.timestamp) - ts(a.timestamp)),
    [appRecords]
  );

  // Sortable Service/Type/Environments/Last-deploy headers (URL-synced ?sort=&dir=).
  // Descending is the more useful first click for "Last deploy" (most-recent-first).
  const { sortKey, sortDir, toggleSort } = useTableSort<ServiceSortKey>(
    VALID_SERVICE_SORT_KEYS,
    'service',
    'asc',
    key => (key === 'lastDeploy' ? 'desc' : 'asc')
  );
  const sortedServices = useMemo(
    () => sortServiceGroups(services, sortKey, sortDir),
    [services, sortKey, sortDir]
  );

  // Compare mode: multi-select of this app's services (URL-synced ?services=)
  const { services: selectedServices, setServices } = useV2Filters();
  const compareMode = selectedServices.length > 0;

  // Type-qualified so same-named jobs of different types (an `ecs` deploy vs.
  // a `terraform` plan both named "ecs") are independently selectable —
  // selecting one no longer silently pulls in the other. serviceCompareLabel
  // renders both halves (`jobId:type`) so the two don't look identical here.
  const serviceNameOptions = useMemo(
    () => services.map(s => serviceFilterKey(s.type, s.serviceName)),
    [services]
  );

  const compareGroups = useMemo(() => {
    if (!compareMode) return [];
    const selected = new Set(selectedServices);
    return sortedServices.filter(s => selected.has(serviceFilterKey(s.type, s.serviceName)));
  }, [compareMode, selectedServices, sortedServices]);

  return (
    <Box>
      <PageHeader
        crumbs={[{ label: 'Services', to: '/services' }, { label: app }]}
        title={app}
        subtitle={`${services.length} service${services.length !== 1 ? 's' : ''}`}
        actions={
          <Button
            component={RouterLink}
            to={`/jobs?apps=${encodeURIComponent(app)}`}
            size="small"
            variant="outlined"
            startIcon={<JobsIcon sx={{ fontSize: 15 }} />}
          >
            Job definitions
          </Button>
        }
      />

      {/* Deploying now — pipeline progress for any active deploy in this app */}
      {deployingRecords.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ color: '#0b76d1', display: 'block', mb: 1 }}>
            Deploying now
          </Typography>
          <Paper
            variant="outlined"
            sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden' }}
          >
            {deployingRecords.map((r, i) => (
              <DeploymentRow key={deploymentKey(r, i)} record={r} progress index={i} />
            ))}
          </Paper>
        </Box>
      )}

      {/* Service multi-select — compare this app's services side by side */}
      {services.length > 0 && (
        <Autocomplete<string, true>
          multiple
          size="small"
          options={serviceNameOptions}
          value={selectedServices.filter(s => serviceNameOptions.includes(s))}
          onChange={(_, next) => setServices(next)}
          getOptionLabel={serviceCompareLabel}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={serviceCompareLabel(option)}
                  size="small"
                  sx={{
                    height: 22,
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    backgroundColor: '#8155b814',
                    color: theme.palette.mode === 'dark' ? c.ink : '#8155b8',
                    border: '1px solid #8155b844',
                  }}
                />
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              placeholder={selectedServices.length === 0 ? 'Select services to compare…' : ''}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ fontSize: 17, color: c.inkFaint, ml: 0.5, mr: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ minWidth: 300, maxWidth: 560, mb: 2.5 }}
        />
      )}

      {services.length === 0 ? (
        <EmptyState
          icon={<ServicesIcon />}
          title="No deployments found for this app"
          body="Either the app name in the URL is wrong, or this app hasn't deployed anything yet."
          action={
            <Button component={RouterLink} to="/services" variant="outlined" size="small">
              Back to services
            </Button>
          }
        />
      ) : compareMode ? (
        compareGroups.length === 0 ? (
          <EmptyState
            icon={<ServicesIcon />}
            title="No matching services in this app"
            body="The selected services don't exist under this app. Clear the selection to see the full table."
            action={
              <Chip
                label="Clear selection"
                size="small"
                onClick={() => setServices([])}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              />
            }
          />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 1.5,
            }}
          >
            {compareGroups.map(group => (
              <ServiceCard key={serviceFilterKey(group.type, group.serviceName)} app={app} group={group} />
            ))}
          </Box>
        )
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === 'service'}
                      direction={sortKey === 'service' ? sortDir : 'asc'}
                      onClick={() => toggleSort('service')}
                    >
                      Service
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === 'type'}
                      direction={sortKey === 'type' ? sortDir : 'asc'}
                      onClick={() => toggleSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Sorted by furthest environment reached (dev → qa/test → stage → prod)" arrow>
                      <TableSortLabel
                        active={sortKey === 'envs'}
                        direction={sortKey === 'envs' ? sortDir : 'asc'}
                        onClick={() => toggleSort('envs')}
                      >
                        Environments
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === 'lastDeploy'}
                      direction={sortKey === 'lastDeploy' ? sortDir : 'asc'}
                      onClick={() => toggleSort('lastDeploy')}
                      sx={{ flexDirection: 'row-reverse' }}
                    >
                      Last deploy
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedServices.map(svc => {
                  const envs = sortEnvNames(Array.from(svc.byEnv.keys()));
                  const servicePath = serviceHref(app, svc.serviceName, svc.type);
                  return (
                    <TableRow
                      key={serviceFilterKey(svc.type, svc.serviceName)}
                      hover
                      onClick={() => navigate(servicePath)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(servicePath);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StatusDot
                            status={
                              svc.status === 'deploying'
                                ? 'inprogress'
                                : svc.status === 'failures'
                                ? 'failed'
                                : svc.status === 'healthy'
                                ? 'success'
                                : svc.status === 'pending'
                                ? 'pending'
                                : undefined
                            }
                          />
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {svc.serviceName}
                          </Typography>
                          {svc.status === 'deploying' && <StatusPill status="inprogress" />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TypeBadge type={svc.type} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          {envs.map(env => {
                            const rec = svc.byEnv.get(env)!;
                            return (
                              <Tooltip
                                key={env}
                                title={`${env}: ${rec.version || '—'} (${rec.status || 'unknown'})`}
                                arrow
                              >
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 0.9,
                                    py: 0.2,
                                    borderRadius: '4px',
                                    border: `1px solid ${c.hairline}`,
                                    backgroundColor: c.canvasSoft,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      backgroundColor: getStatusHue(rec.status),
                                    }}
                                  />
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: c.inkSecondary }}>
                                    {env}
                                    {rec.ttl != null ? ' ⏳' : ''}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" sx={{ color: c.inkMuted }}>
                          {svc.lastTimestamp ? timeAgo(svc.lastTimestamp) : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default AppPage;
